import React, { useEffect, useRef } from 'react'
import { CharCount } from './CharCount'

interface InlineEditFieldProps {
  /** Single-line title field */
  title?: string
  onTitleChange?: (v: string) => void
  titleMin?: number
  titleMax?: number
  titlePlaceholder?: string

  /** Multi-line body field */
  body?: string
  onBodyChange?: (v: string) => void
  bodyMin?: number
  bodyMax?: number
  bodyPlaceholder?: string
  bodyRows?: number

  onSave: () => void
  onCancel: () => void
  saving?: boolean
  saveLabel?: string
  cancelLabel?: string
}

export const InlineEditField: React.FC<InlineEditFieldProps> = ({
  title, onTitleChange, titleMin = 10, titleMax = 300, titlePlaceholder = 'Title',
  body,  onBodyChange,  bodyMin  = 20, bodyMax  = 10000, bodyPlaceholder = 'Content', bodyRows = 5,
  onSave, onCancel, saving = false, saveLabel = 'Save changes', cancelLabel = 'Cancel',
}) => {
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => { titleRef.current?.focus() }, [])

  const titleOk = title === undefined || (title.trim().length >= titleMin && title.trim().length <= titleMax)
  const bodyOk  = body  === undefined || (body.trim().length  >= bodyMin  && body.trim().length  <= bodyMax)
  const canSave = titleOk && bodyOk && !saving

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onCancel(); return }
    if (e.key === 'Enter' && e.ctrlKey && canSave) { onSave() }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {title !== undefined && onTitleChange && (
        <div>
          <input
            ref={titleRef}
            value={title}
            onChange={e => onTitleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={titlePlaceholder}
            maxLength={titleMax}
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              background: 'var(--bg-base)', border: '1px solid var(--accent-primary)',
              borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem',
              fontWeight: 600, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
              marginBottom: '0.2rem',
            }}
          />
          <CharCount current={title.trim().length} max={titleMax} min={titleMin} />
        </div>
      )}

      {body !== undefined && onBodyChange && (
        <div>
          <textarea
            value={body}
            onChange={e => onBodyChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={bodyPlaceholder}
            rows={bodyRows}
            maxLength={bodyMax}
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              background: 'var(--bg-base)', border: '1px solid var(--border)',
              borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.875rem',
              fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
              marginBottom: '0.2rem',
            }}
          />
          <CharCount current={body.trim().length} max={bodyMax} min={bodyMin} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '0.4rem 0.9rem', background: 'transparent',
            border: '1px solid var(--border)', borderRadius: '8px',
            color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
          }}
        >
          {cancelLabel}
        </button>
        <button
          onClick={onSave}
          disabled={!canSave}
          style={{
            padding: '0.4rem 1rem',
            background: canSave ? 'var(--accent-primary)' : 'var(--bg-elevated)',
            color: canSave ? 'white' : 'var(--text-muted)',
            border: 'none', borderRadius: '8px', fontWeight: 600,
            cursor: canSave ? 'pointer' : 'not-allowed', fontSize: '0.8rem',
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {saving ? 'Saving...' : saveLabel}
        </button>
      </div>
    </div>
  )
}
